import {TrackingManagerV1} from '@volkswagen-onehub/tracking-service'
import store from '../app/store'
import {getFetchedDealerFromCode} from './utilities'
import {DealerData} from '../types/dealer'

let localTrackingManager: TrackingManagerV1

export const setTrackingServiceManager = (
    trackingManager: TrackingManagerV1
) => {
    localTrackingManager = trackingManager
}

export enum TrackingAction {
    VW_BASIC_FORM_ERROR_MESSAGE_LOAD = 'VWBasic_FormErrorMessage_Load',
    VW_BASIC_FORM_SUBMISSION_SUCCESS_MESSAGE_LOAD = 'VWBasic_FormSubmissionSuccessMessage_Load',
    VW_BASIC_LINK_CLICK = 'VWBasic_Link_Click',
    VW_BASIC_MODEL_SELECT_CLICK = 'VWBasic_ModelSelect_Click',
    VW_BASIC_BUTTON_CLICK = 'VWBasic_Button_Click',
    VW_BASIC_FORM_LAYER_LOAD = 'VWBasic_FormLayer_Load',
    VW_BASIC_FORM_LAYER_CLOSE_CLICK = 'VWBasic_FormLayerClose_Click',
    VW_BASIC_FORM_CTA_CLICK = 'VWBasic_FormCTA_Click',
    VW_BASIC_TEASER_CLICK = 'VWBasic_Teaser_Click',
}

export const trackingData = {
    APPLICATION_ID: 'VWCMSD6',
    FEATURE_APP_ID: 'Voucher-D6-IT',
    FEATURE_APP_ID_VERSION: '2.2.0',
    BRAND: {
        VW: 'passenger',
        VI: 'commercial',
    },
    DEALER_INFORMATION_BRAND: {
        VW: 'V',
        VI: 'N',
    },
    DEALER_INFORMATION_COUNTRY: 'ITA',
    FORM_TYPE: 'servicerequest',
    STEP_NAME: 'Thank you page',
    STEP_NUMBER: '2',
    LEAD_ID: 'IT-SFACPN-',
    COUNTRY: 'IT',
    LANGUAGE: 'it',
    COMMERCE: false,
    PERSONALIZATION: false,
    LOGIN_STATUS: false,
    ORIENTATION_HORIZONTAL: 'h',
    ORIENTATION_VERTICAL: 'v',
    PAGE_CATEGORY: 'Area Clienti',
    DEFAULT_MODEL: {
        VW: 'Gamma Volkswagen',
        VI: 'Gamma VIC',
    },
}

const globalWindow = window as unknown as {vwdata?: unknown}

const getVwData = (): any[] => {
    const candidate: any = globalWindow.vwdata

    if (Array.isArray(candidate)) {
        return candidate
    }

    if (candidate && typeof candidate.getEventList === 'function') {
        const eventList = candidate.getEventList()

        return Array.isArray(eventList) ? eventList : []
    }

    return []
}

const pushToVwData = (entry: unknown): void => {
    const candidate: any = globalWindow.vwdata
    if (candidate && typeof candidate.push === 'function') {
        candidate.push(entry)
    }
}

type BrandKey = keyof typeof trackingData.BRAND
interface TrackingConfiguration {
    environment?: Record<string, any>
    eventInfo?: Record<string, any>
    partner?: Record<string, any>
    personalization?: Record<string, any>
    user?: Record<string, any>
    [key: string]: any
}
type TrackingPayload = TrackingConfiguration & {
    environment: Record<string, any>
    eventInfo: Record<string, any>
    personalization: Record<string, any>
    user: Record<string, any>
}
const getBrandKey = (brand?: string): BrandKey =>
    brand && brand.toUpperCase() === 'VI' ? 'VI' : 'VW'

export const eventInfoCommon = () => ({
    featureAppId: trackingData.FEATURE_APP_ID,
    brand: trackingData.BRAND[
        getBrandKey(store.getState().configurationPromo.configuration.brand)
    ],
})

export const defaultModel = () =>
    !store.getState().userData.modelDisplayName
        ? trackingData.DEFAULT_MODEL[
              getBrandKey(
                  store.getState().configurationPromo.configuration.brand
              )
          ]
        : store.getState().userData.modelDisplayName

const getPageName = (applicationPageName: string) =>
    `app : ${applicationPageName}`

const isFeatureAppFallbackPageName = (pageName?: string) =>
    typeof pageName === 'string' && pageName.toLowerCase().startsWith('app :')

const hasPageContext = (entry: any) =>
    entry?.eventInfo?.pageName ||
    entry?.eventInfo?.pageCategory ||
    entry?.eventInfo?.pageId

const getPageContextFromVwData = () => {
    const safeVwData = getVwData()
    const reversedEventList = [...safeVwData].reverse()
    const pageContext =
        reversedEventList.find(
            (entry: any) =>
                hasPageContext(entry) &&
                !isFeatureAppFallbackPageName(entry?.eventInfo?.pageName)
        ) || reversedEventList.find(hasPageContext)

    return {
        pageName: pageContext?.eventInfo?.pageName,
        pageCategory: pageContext?.eventInfo?.pageCategory,
        pageId:
            pageContext?.eventInfo?.pageId ||
            window.location.host + window.location.pathname,
    }
}

const getFeatureAppIdAppVersion = (
    trackingConfiguration: TrackingConfiguration
) => {
    const contextualEnvironment = localTrackingManager?.contextualEventData
        ?.event?.environment as Record<string, any> | undefined
    const eventListEnvironment = [...getVwData()]
        .reverse()
        .find(
            (entry: any) => entry?.environment?.featureAppIdAppVersion
        )?.environment

    return (
        trackingConfiguration.environment?.featureAppIdAppVersion ||
        eventListEnvironment?.featureAppIdAppVersion ||
        contextualEnvironment?.featureAppIdAppVersion ||
        trackingData.FEATURE_APP_ID_VERSION
    )
}

const getBreakpoint = () => {
    let breakpoint = ''

    if (window.innerWidth < 480) {
        breakpoint = '0'
    } else if (window.innerWidth >= 480 && window.innerWidth < 639) {
        breakpoint = '480'
    } else if (window.innerWidth >= 639 && window.innerWidth < 1024) {
        breakpoint = '639'
    } else if (window.innerWidth >= 1024 && window.innerWidth < 1281) {
        breakpoint = '1024'
    } else if (window.innerWidth >= 1281) {
        breakpoint = '1281'
    }

    return breakpoint
}

const getBrowserOrientation = () =>
    window.innerHeight > window.innerWidth
        ? trackingData.ORIENTATION_VERTICAL
        : trackingData.ORIENTATION_HORIZONTAL

const getBrowserData = () => ({
    browserResolutionHeight: document.documentElement.clientHeight.toString(),
    browserResolutionWidth: document.documentElement.clientWidth.toString(),
    browserResolutionBreakpoint: getBreakpoint(),
    browserResolutionOrientation: getBrowserOrientation(),
})

const getDealerInformationBrand = (dealer?: DealerData | null) =>
    trackingData.DEALER_INFORMATION_BRAND[
        getBrandKey(
            dealer?.brand ||
                store.getState().configurationPromo.configuration.brand ||
                store.getState().general.brand
        )
    ]

const getDealerType = (dealer: DealerData): string => {
    const dealerTypeSource = [
        dealer.type,
        dealer.activity,
        dealer.contract,
        dealer.specialization,
    ]
        .filter(Boolean)
        .join(' ')
        .toUpperCase()

    if (dealerTypeSource.includes('TRADEPORT')) {
        return 'TRADEPORT'
    }
    if (
        (dealerTypeSource.includes('SERVICE_ONLY') ||
            dealerTypeSource.includes('SERVICE ONLY') ||
            dealerTypeSource.includes('SERVICE') ||
            dealerTypeSource.includes('ASSISTENZA')) &&
        !dealerTypeSource.includes('SALES') &&
        !dealerTypeSource.includes('VENDITA')
    ) {
        return 'SERVICE_ONLY'
    }
    if (
        (dealerTypeSource.includes('SALES_ONLY') ||
            dealerTypeSource.includes('SALES ONLY') ||
            dealerTypeSource.includes('SALES') ||
            dealerTypeSource.includes('VENDITA')) &&
        !dealerTypeSource.includes('SERVICE') &&
        !dealerTypeSource.includes('ASSISTENZA')
    ) {
        return 'SALES_ONLY'
    }

    return 'FULLSTACK'
}

const getDealerInformationType = (dealer: DealerData) => {
    const isCommercial =
        getBrandKey(
            dealer.brand ||
                store.getState().configurationPromo.configuration.brand ||
                store.getState().general.brand
        ) === 'VI'
    const dealerType = getDealerType(dealer)

    if (isCommercial) {
        switch (dealerType) {
            case 'SERVICE_ONLY':
                return 'WS'
            case 'SALES_ONLY':
                return 'HNB'
            case 'TRADEPORT':
                return 'NVB'
            default:
                return 'HSB'
        }
    }

    switch (dealerType) {
        case 'SERVICE_ONLY':
            return 'WS'
        case 'SALES_ONLY':
            return 'HN'
        default:
            return 'HS'
    }
}

export const getDealerUserInfo = (dealer?: DealerData | null) => {
    const selectedDealer =
        dealer || (store.getState().dealers.selected as DealerData | null)

    if (!selectedDealer) {
        return {
            dealerStatus: false,
        }
    }

    return {
        dealerInformationBnr: selectedDealer.companyCode,
        dealerInformationBrand: getDealerInformationBrand(selectedDealer),
        dealerInformationCountry: trackingData.DEALER_INFORMATION_COUNTRY,
        dealerInformationName: selectedDealer.company,
        dealerInformationType: getDealerInformationType(selectedDealer),
        dealerInformationZIP: selectedDealer.zipCode,
        dealerStatus: true,
    }
}

const getPartnerInfo = (dealer?: DealerData | null) =>
    dealer
        ? {
              informationBnr: dealer.companyCode,
              informationBrand: getDealerInformationBrand(dealer),
              informationCountry: trackingData.DEALER_INFORMATION_COUNTRY,
              informationName: dealer.company,
              informationType: getDealerInformationType(dealer),
              informationZIP: dealer.zipCode,
          }
        : undefined

const getTrackingDealer = (state: ReturnType<typeof store.getState>) => {
    const selectedDealer = state.dealers.selected as DealerData | null

    if (selectedDealer) {
        return selectedDealer
    }

    if (!state.general.isDealerWeb) {
        return null
    }

    return getFetchedDealerFromCode(
        state.dealers.myDealerBranches,
        state.dealers.myDealerCode
    )
}

const buildTrackingPayload = (
    event: string,
    trackingConfiguration: TrackingConfiguration,
    state: ReturnType<typeof store.getState>
): TrackingPayload => {
    const promoConfig = state.configurationPromo.configuration
    const isLoggedDefined = typeof state.general.logged !== 'undefined'
    const pageContext = getPageContextFromVwData()
    const trackingDealer = getTrackingDealer(state)
    const partner = getPartnerInfo(trackingDealer)

    return {
        ...trackingConfiguration,
        environment: {
            ...(trackingConfiguration.environment || {}),
            applicationId: trackingData.APPLICATION_ID,
            featureAppVariant: promoConfig.tracking_application_id,
            featureAppId: trackingData.FEATURE_APP_ID,
            featureAppIdAppVersion: getFeatureAppIdAppVersion(
                trackingConfiguration
            ),
            country: trackingData.COUNTRY,
            language: trackingData.LANGUAGE,
            commerce: trackingData.COMMERCE,
        },
        ...(partner || trackingConfiguration.partner
            ? {
                  partner: {
                      ...(partner || {}),
                      ...(trackingConfiguration.partner || {}),
                  },
              }
            : {}),
        user: {
            loginStatus: isLoggedDefined
                ? state.general.logged
                : trackingData.LOGIN_STATUS,
            ...getBrowserData(),
            ...(trackingDealer ? getDealerUserInfo(trackingDealer) : {}),
            ...(trackingConfiguration.user || {}),
        },
        personalization: {
            status: trackingData.PERSONALIZATION,
            ...(trackingConfiguration.personalization || {}),
        },
        eventInfo: {
            ...(trackingConfiguration.eventInfo || {}),
            eventAction: event,
            pageName:
                trackingConfiguration.eventInfo?.pageName ||
                pageContext.pageName ||
                getPageName(promoConfig.name),
            pageCategory:
                trackingConfiguration.eventInfo?.pageCategory ||
                pageContext.pageCategory ||
                trackingData.PAGE_CATEGORY,
            pageId:
                trackingConfiguration.eventInfo?.pageId || pageContext.pageId,
        },
    }
}

const trackWithContextualOverrides = (
    event: string,
    trackingPayload: TrackingPayload
) => {
    const previousContextualEventData = localTrackingManager.contextualEventData
    const previousContextualEventInfo =
        previousContextualEventData?.event?.eventInfo

    try {
        localTrackingManager.contextualEventData = {
            ...previousContextualEventData,
            featureAppId: trackingPayload.environment.featureAppId,
            event: {
                ...(previousContextualEventData?.event || {}),
                environment: {
                    ...(previousContextualEventData?.event?.environment || {}),
                    featureAppId: trackingPayload.environment.featureAppId,
                    featureAppIdAppVersion:
                        trackingPayload.environment.featureAppIdAppVersion,
                    featureAppVariant:
                        trackingPayload.environment.featureAppVariant,
                } as any,
                eventInfo: {
                    ...(previousContextualEventInfo || {}),
                    pageCategory: trackingPayload.eventInfo.pageCategory,
                    pageId: trackingPayload.eventInfo.pageId,
                    pageName: trackingPayload.eventInfo.pageName,
                },
            },
        }

        return localTrackingManager.track(event, trackingPayload)
    } finally {
        localTrackingManager.contextualEventData = previousContextualEventData
    }
}

export const trackingEvent = (event: string, configuration?: any) => {
    try {
        const trackingConfiguration = configuration || {}
        const state = store.getState()
        const trackingPayload = buildTrackingPayload(
            event,
            trackingConfiguration,
            state
        )

        if (!state.general.isDealerWeb) {
            return trackWithContextualOverrides(event, trackingPayload)
        }

        pushToVwData(trackingPayload)

        return undefined
    } catch (err) {
        console.warn('[trackingEvent] tracking failed:', err)

        return undefined
    }
}
